import { ProjectCollection } from "Presentation/Scripts/TFS/TFS.OM.Common";
import * as Q from "q";
import StringUtils = require("VSS/Utils/String");
import { WidgetsCacheableQueryService } from "Widgets/Scripts/DataServices/WidgetsCacheableQueryService";
import { ICacheableQuery } from "Analytics/Scripts/QueryCache/ICacheableQuery"
import WidgetResources = require("Widgets/Scripts/Resources/TFS.Resources.Widgets");
import { ProcessesEntry, ProcessesQuery } from "Analytics/Scripts/QueryCache/ProcessesQuery";


export declare class BacklogCategoryInformation {
    category: string;
    workItemTypeNames: string[];
    plural: string;
    isRequirementCategory: boolean;
}

/** Legacy Helper class for dealing with Team and project scoped WIT settings.
 * New consumers should go directly to the Ax-backed "ProcessesQuery".
 */
export class SettingsHelper {
    public static WitRequirementsCategory = "Microsoft.RequirementCategory";

    public static runQueryOnDashboardCache<T>(query: ICacheableQuery<T>): IPromise<T> {
        let dataService = ProjectCollection.getDefaultConnection().getService(WidgetsCacheableQueryService);
        return dataService.getCacheableQueryResult(query);
    }

    /**
     * Retrieves the work item types associated with the given backlog category.
     * Includes work items from the bugs category if the backlog passed in is the requirements backlog, and the
     * team settings for the given team ID are set to show bugs on the requirements backlog.
     * 
     * Data is sourced from an Ax backed Cachable Query, or WIT, based on feature flag state.
     * 
     * @param projectId The project ID to use when looking up the process configuration
     * @param teamId  The team to use when looking up team settings
     * @param backlogCategory The backlog category for which to look up the associated work item types
     */
    public static getTeamWorkItemTypesOfBacklogCategory(projectId: string, teamId: string, backlogCategory: string): IPromise<string[]> {
        var types: IPromise<string[]>;
        //Get team Process, and then select the WIT's associated with the requested backlog Category.
        types = SettingsHelper.runQueryOnDashboardCache(ProcessesQuery.onTeam(projectId, teamId)).then(types => {
            return types.map((entry, i) => {
                return (entry.BacklogCategoryReferenceName === backlogCategory) ? entry.WorkItemType : null;
            }).filter(o => o != null);
        });
        return types;
    }

    /**
     * Retrieves the work item types associated with the given backlog category, at context of a project.
     * Includes work items from the bugs category if the backlog passed in is the requirements backlog, and the
     * includeBugsForRequirementCategory argument is true.
     * @param projectId The project ID to use when looking up the process configuration
     * @param backlogCategory The backlog category for which to look up the associated work item types
     * @param includeBugsForRequirementCategory Defaults to false. Set to true if adding bugs category work item types to the requirements category work item types is desired.
     */
    public static getProjectWorkItemTypesOfBacklogCategory(projectId: string, backlogCategory: string, includeBugsForRequirementCategory?: boolean): IPromise<string[]> {
        var types: IPromise<string[]>;

        //Get team Process, and then select the WIT's associated with the requested backlog Category.
        //Include items of the "bugs" Category if "includeBugsForRequirementCategory" is true
        //Filters out duplicates, since the query is project-scoped but each team can have their own configuration
        //NOTE: WIT's can be associated with the backlog and marked as bug types. The "Basic" process "Issue" is an OOB example of this. These WIT's should be included regardless of includeBugsForRequirementCategory.
        types = SettingsHelper.runQueryOnDashboardCache(ProcessesQuery.onProject(projectId)).then(types => {
            let result : string[] = [];
            for (let entry of types) {
                const isOfBacklogCategory = (entry.BacklogCategoryReferenceName === backlogCategory);
                const includeAsABug = (entry.IsBugType
                    && includeBugsForRequirementCategory
                    && backlogCategory === SettingsHelper.WitRequirementsCategory);
                if ((isOfBacklogCategory || includeAsABug) && result.indexOf(entry.WorkItemType) < 0) {
                    result.push(entry.WorkItemType);
                }
            };
            return result;
        });
        return types;
    }

    /**
     * Retrieves the plural name of the given backlog category.
     * @param projectId The project ID to use when looking up the process configuration
     * @param backlogCategory The backlog category for which to retrieve the plural name
     */
    public static getPluralBacklogNameOfBacklogCategory(projectId: string, backlogCategory: string): IPromise<string> {
        const getMatchFailure = (backlogCategory: string) => { return Q.reject<string>(`Failed to find the backlog category '${backlogCategory}' in the process configuration.`) };
        var promise: IPromise<string>;
        
        promise = SettingsHelper.runQueryOnDashboardCache(ProcessesQuery.onProject(projectId)).then(types => {
            var result: ProcessesEntry = types.find((entry, i) => {
                return entry.BacklogCategoryReferenceName === backlogCategory;
            });
            return result != null ? result.BacklogName : getMatchFailure(backlogCategory);
        });        

        promise.then<string>(undefined, error => {
            return Q.reject(StringUtils.format(WidgetResources.ModernWidget_PluralBacklogNameErrorFormat, error));
        });
        return promise;
    }
}